import type { CSSProperties, PropsWithChildren, Ref } from 'react';

import { cn } from 'cn';

type P = PropsWithChildren<{
  /** The nesting level (1 = root level) */
  level?: number;
  /** Positions the active indicator; the indicator is hidden when undefined */
  indicatorStyle?: CSSProperties;
  /** Receives the root list element so its geometry can be measured */
  rootRef?: Ref<HTMLUListElement>;
  /** Enables the slide transition for the active indicator */
  shouldAnimate?: boolean;
}> &
  ComponentStyling;

/**
 * Container for tree items at a given level.
 * The root level owns the vertical rail and the active indicator; nested
 * levels are indented relative to their parent item.
 */
export function TreeItemGroup({
  children,
  className,
  indicatorStyle,
  level = 1,
  rootRef,
  shouldAnimate = false,
  style,
}: P) {
  const isRoot = level === 1;

  return (
    <ul
      className={cn(
        'w-full',
        {
          'relative mt-px border-l-3 border-tiga-border-lighter': isRoot,
          'pl-4': !isRoot,
        },
        className,
      )}
      data-tree-viewer-root={isRoot || undefined}
      ref={isRoot ? rootRef : undefined}
      style={style}
    >
      {isRoot && indicatorStyle ? (
        <li
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute top-0 -left-0.75 w-0.75 bg-tiga-action-default',
            {
              'transition-all duration-200 ease-out motion-reduce:transition-none':
                shouldAnimate,
            },
          )}
          data-testid="tree-active-indicator"
          style={indicatorStyle}
        />
      ) : null}
      {children}
    </ul>
  );
}
